import { computed, inject, Injectable, Signal } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { EntityFacadeService } from "../../../../shared/store/entity-store/entity-facade.service";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { Untrack, UpdateStatus } from "../../../../shared/store/utils/store-utils.service";
import { Cost } from "../../models/cost.model";
import { CostApiService } from "./cost-api.service";
import { CostState, CostStoreService } from "./cost-store.service";

@Injectable({ providedIn: "root" })
export class CostFacadeService extends EntityFacadeService<
  Cost,
  CostApiService,
  CostState,
  EntityMetadata,
  CostStoreService
> {
  constructor() {
    const api = inject(CostApiService);
    const store = inject(CostStoreService);
    super(api, store);
    this.store.initStatus(CostFacadeService);
  }

  @Untrack
  @UpdateStatus
  async loadForLot(lotId: string) {
    const costs = await firstValueFrom(this.api.loadForLot(lotId));
    this.store.addMany(costs);
    return costs;
  }

  @Untrack
  @UpdateStatus
  async upsertCost(cost: Cost) {
    const result = await firstValueFrom(this.api.update(cost));
    this.store.upsertOne(result);
    return result;
  }

  @Untrack
  @UpdateStatus
  async deleteCost(costId: string) {
    await firstValueFrom(this.api.remove(costId));
    this.store.removeOne(costId);
  }

  @Untrack
  @UpdateStatus
  async loadCascadeImpact(lotId: string) {
    return await firstValueFrom(this.api.loadCascadeImpact(lotId));
  }

  @Untrack
  @UpdateStatus
  async triggerCascade(lotId: string) {
    return await firstValueFrom(this.api.triggerCascade(lotId));
  }

  getLastCostForLot(lotId: string): Signal<Cost | undefined> {
    return computed(() => {
      const allCosts = this.getAllEntities()();
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      const lotCosts = allCosts.filter(c =>
        c.lotId === lotId && new Date(c.date).getTime() <= today.getTime(),
      );
      if (!lotCosts.length) return undefined;
      return lotCosts.sort((a, b) =>
        new Date(b.date).getTime() - new Date(a.date).getTime(),
      )[0];
    });
  }
}
